import React, { useState, useCallback } from 'react';

import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import Button from 'components/button/button';

import type { PeriodJob } from 'state/remote/types';

import { createLocale } from 'locales';

import {
  formatJobDurationDisplay,
  formatRates,
} from 'utils/display-text-formatter';

import Panel from 'pages/freelancer/FreelancerAvailableJobs/components/collapsiblePanel/Panel';

import InfoItem from 'pages/freelancer/FreelancerAvailableJobs/components/JobList/InfoItem';

import useFreelancerInterestedInPeriodJob from 'state/remote/remote-freelancer/use-freelancer-interested-in-period-job';

import useFreelancerRefusePeriodJob from 'state/remote/remote-freelancer/use-freelancer-refuse-period-job';

import ConfirmationDialog from 'pages/freelancer/FreelancerAvailableJobs/components/confirmationDialog/ConfirmationDialog';

import CardHeader from './CardHeader';

const localize = createLocale('freelancerAvailableJobsLocales');

interface PropTypes {
  job?: PeriodJob;
  vatExempt: boolean;
}

interface formatRateReturnType {
  displayText: string;
  useHighlight: boolean;
}

const PeriodJobCard: React.FC<PropTypes> = ({ job, vatExempt }) => {
  const refusePeriodJob = useFreelancerRefusePeriodJob();
  const interestedJob = useFreelancerInterestedInPeriodJob();
  let salaryDisplay: formatRateReturnType = {} as formatRateReturnType;
  let headerTitle = '';
  const [action, setAction] = useState<'refuse' | 'interest' | undefined>(
    undefined,
  );

  const onActionConfirmed = useCallback(() => {
    if (job) {
      switch (action) {
        case 'interest':
          interestedJob.mutate({ jobId: job.id });
          setAction(undefined);
          break;
        case 'refuse':
          refusePeriodJob.mutate({ jobId: job.id });
          setAction(undefined);
          break;
        default:
          break;
      }
    }
  }, [job, action, interestedJob, refusePeriodJob]);

  if (job) {
    headerTitle = `${job.organisation}, (${
      job.travel_distance ? job.travel_distance.toFixed(1) : '?'
    } km) `;
    salaryDisplay = formatRates(
      job.max_rate,
      job.max_rate_incl,
      job.max_rate_exempt,
      job.actual_rate,
      job.actual_rate_incl,
      job.actual_rate_exempt,
      vatExempt,
    );
  }
  if (!job) return <></>;
  const interestedBtnClassName = twMerge(
    'w-42 flex items-center',
    'mr-1 justify-center py-2 text-white bg-lightBlue',
    'hover:bg-darkBlue rounded-l-md border-slate300 rounded-r-none',
    job.is_interested && 'cursor-not-allowed',
  );
  return (
    <div className="container flex flex-col w-full my-3 px-0 cursor">
      <Panel
        Header={CardHeader}
        headerTitle={headerTitle}
        headerClassName="bg-gray50 hover:bg-gray75"
      >
        <InfoItem
          textInfo={`${job.location}, ${job.full_address}`}
          iconName="locationN"
          textClassName="text-lightBlue"
          isLink
          url={`https://maps.google.com/?q=${job.full_address}`}
        />
        <InfoItem
          textInfo={job?.parking_free}
          iconName="local_parking"
          textClassName="text-purple"
        />
        <InfoItem
          textInfo={`${job.from_date
            .split('-')
            .reverse()
            .join('-')}   -   ${job.to_date.split('-').reverse().join('-')}`}
          iconName="calendar"
          textClassName="text-purple"
        />
        <InfoItem
          textInfo={job.weekdays ? job.weekdays.join(', ') : ''}
          iconName="cloud"
        />
        <InfoItem
          textInfo={formatJobDurationDisplay(
            job.start_time,
            job.end_time,
            job.pause.toString(),
          )}
          iconName="clock"
        />
        <InfoItem textInfo={job.competence} iconName="wrench" />
        <InfoItem
          iconName="license-1"
          textInfo={
            job.certificates.length > 0
              ? job.certificates.join(', ')
              : localize('noCertificatesRequired')
          }
        />
        <InfoItem
          textInfo={salaryDisplay.displayText || ''}
          iconName="euro"
          textClassName={
            salaryDisplay.useHighlight ? 'text-brown text-body-large-bold' : ''
          }
        />
        <InfoItem
          textInfo={job.remarks && job.remarks !== '' ? job.remarks : '-'}
          iconName="message1"
        />
        <div className="flex flex-row items-center justify-end mt-4 mr-5 mb-3">
          <Button
            label={localize('interested')}
            disabled={job.is_interested}
            variant="primary"
            className={interestedBtnClassName}
            onClick={() => {
              setAction('interest');
            }}
          />

          <Button
            label={localize('toRefuse')}
            variant="outlined"
            className="w-32 flex items-center justify-center py-2 hover:bg-gray100 border-slate300 mr-1 rounded-l-none rounded-r-md"
            onClick={() => {
              setAction('refuse');
            }}
          />
        </div>
      </Panel>
      <ConfirmationDialog
        isOpen={!!action}
        onCloseClicked={() => {
          setAction(undefined);
        }}
        onConfirm={onActionConfirmed}
        message={classNames(
          action === 'interest' && localize('confirmInterestPeriod'),
          action === 'refuse' && localize('confirmRefusePeriod'),
        )}
      />
    </div>
  );
};

export default PeriodJobCard;
